import { NetworkLocation, TourStopSelection } from '../types';
import { cityOf } from './tokovi';

/**
 * Sastav ture od izabranih tokova i isporuka.
 *
 * Izdvojeno iz TourBuilder-a da bi se moglo TESTIRATI: ovde se odlučuje šta
 * vozilo nosi, gde staje i kojim redom — tačno ono što ne sme da se pokvari,
 * a živelo je u komponenti koju nijedan test ne vidi.
 */

/** Jedna tačka obilaska, za crtanje linije ture. */
export interface TourPoint {
  key: string;
  label: string;
  latitude: number | null;
  longitude: number | null;
  isPickup: boolean;
}

export interface DraftItemShape {
  articleCode: string;
  articleName: string;
  quantity: number;
  erpDocument: string | null;
}

export interface DraftStopShape {
  kind: number;
  locationKind?: number;
  locationCode?: string;
  customerAddressId?: number;
  isPickup?: boolean;
  items: DraftItemShape[];
}

export interface TourComposition {
  draftStops: DraftStopShape[];
  keys: string[];
  merged: Map<string, string[]>;
  sequence: TourPoint[];
  /** Za svako preuzimanje toka: koliko nosi i kuda ta roba dalje ide. */
  pickupSummary: Map<string, { pieces: number; feeds: string[]; toWarehouse: boolean }>;
}

export interface CompositionOptions {
  /**
   * Odakle tura kreće i gde se vraća, punom oznakom — „MAG 021" ili
   * „MPO 004".
   *
   * Puna oznaka, a ne šifra, otkad polazište sme da bude i prodavnica: sa
   * golom šifrom „004" se ovde tražilo „MAG 004", pa bi sopstvena roba
   * izgledala kao tuđa i dobila stanicu preuzimanja na mestu gde vozilo već
   * stoji.
   */
  base: string;
  /** Mesta učitanog pravca. Odredište na pravcu se isporučuje, ne pretovara. */
  routeDestinations: string[];
  /** Operaterov izbor za tok van magacina: true = vozi do odredišta. */
  deliverOverrides: Record<string, boolean>;
  locations: NetworkLocation[];
  /**
   * Pun redosled ključeva, sa „pretovar:" ključevima ako su već dobili mesto
   * — od operatera strelicama ili od „Složi redosled". Preuzimanje sa mesta u
   * ovom spisku ulazi tačno tu; ostala ulaze po podrazumevanom pravilu.
   *
   * Bez ovoga se pozicija preuzimanja gubila pri svakom preslaganju: složeni
   * redosled je preuzimanje stavio u prolaz, a ponovni sastav ga vraćao tik
   * pred isporuku — i tura je na mapi išla Kula → Vrbas → Kula.
   */
  orderedKeys?: string[];
}

/** Šifra odredišta iz oznake tipa „MPO 003". */
export function codeOf(destination: string): string | null {
  const match = destination.match(/(\d{3})\s*$/);
  return match ? match[1] : null;
}

/**
 * Tok traži preuzimanje kad roba ne stoji u polaznom magacinu. Nepoznat
 * izvor se tovari iz magacina — a pregled ture kaže da se izvor ne zna.
 */
export function needsPickup(origin: string | null, base: string): boolean {
  return origin !== null && origin !== base;
}

/**
 * Da li tok ISPORUČUJE na ovoj turi, ili se roba vozi do magacina.
 *
 * Podrazumevano: tok iz magacina uvek isporučuje; odredište na učitanom
 * pravcu se isporučuje (tura tuda svakako ide); tok sa objekta isporučuje i
 * kad na isto odredište ionako ide roba iz magacina. Inače roba ide u magacin
 * — produžetak ture do odredišta je operaterov izbor, prekidačem.
 */
export function deliversHere(
  flowKey: string,
  origin: string | null,
  destination: string,
  selections: TourStopSelection[],
  options: Pick<CompositionOptions, 'base' | 'routeDestinations' | 'deliverOverrides' | 'locations'>
): boolean {
  if (!needsPickup(origin, options.base)) return true;

  // Roba za kupca ide PRAVO u objekat: kupac je čeka tamo, a pretovar preko
  // magacina bi je ostavio u magacinu (vidi DoObjekta na serveru).
  const tok = selections.find((s) => s.key === flowKey);
  if (tok?.kind === 'mpo' && tok.group.zaKupce) return true;

  const override = options.deliverOverrides[flowKey];
  if (override !== undefined) return override;

  if (options.routeDestinations.includes(destination)) return true;

  // Odredište koje JE polazni magacin nema svoju stanicu — istovar u
  // magacin je kraj ture, ne usputno mesto.
  if (destination === options.base) return false;

  // Odredište u polaznom gradu: vozilo se na kraju ionako vraća tu, pa se
  // istovara direktno kod primaoca — druga noga preko magacina bi bila
  // vožnja do istog dvorišta sutradan.
  const startCity = cityOf(options.base, options.locations);
  if (startCity !== null && cityOf(destination, options.locations) === startCity) {
    return true;
  }

  return selections.some((other) =>
    other.kind === 'mpo'
    && other.key !== flowKey
    && !needsPickup(other.group.origin, options.base)
    && other.group.destination === destination);
}

/**
 * Slaže stanice od izbora: kupci i B2C preuzimanja na svoja mesta, više
 * tokova ka istom mestu u JEDNU stanicu, preuzimanje uz stanicu svog mesta a
 * uvek pre isporuke koju hrani. Tok bez isporuke na turi ide na kraj — uzima
 * se u povratku, do magacina.
 */
export function buildTour(
  selections: TourStopSelection[],
  options: CompositionOptions
): TourComposition {
  type Stavka = DraftItemShape;

  const keys: string[] = [];
  const mergedKeys = new Map<string, string[]>();
  const draftStops: DraftStopShape[] = [];

  const kindOf = (reference: string): number => (reference.startsWith('MAG') ? 1 : 0);

  const tokovi = selections.filter(
    (s): s is Extract<TourStopSelection, { kind: 'mpo' }> => s.kind === 'mpo');

  const itemsOf = (tok: Extract<TourStopSelection, { kind: 'mpo' }>): Stavka[] =>
    tok.group.items.map((i) => ({
      articleCode: i.articleCode,
      articleName: i.articleName,
      quantity: i.quantity,
      // Nalog ide uz stavku: po njemu magacioner zna šta pakuje, a kasnije
      // se zna i iz kog dokumenta je roba otišla.
      erpDocument: i.sourceDocument
    }));

  const delivers = (tok: Extract<TourStopSelection, { kind: 'mpo' }>): boolean =>
    deliversHere(tok.key, tok.group.origin, tok.group.destination, selections, options);

  // Više tokova ka istom mestu je JEDNA fizička stanica. Spajanje se računa
  // unapred, a stanica ulazi na mesto PRVOG izabranog toka — redosled
  // izbora je redosled obilaska.
  const spojeno = new Map<string, { items: Stavka[]; flowKeys: string[] }>();

  for (const tok of tokovi) {
    if (!delivers(tok)) continue;

    let delivery = spojeno.get(tok.group.destination);
    if (!delivery) {
      delivery = { items: [], flowKeys: [] };
      spojeno.set(tok.group.destination, delivery);
    }
    delivery.items.push(...itemsOf(tok));
    delivery.flowKeys.push(tok.key);
  }

  const emitted = new Set<string>();

  for (const stop of selections) {
    if (stop.kind === 'kupac') {
      keys.push(stop.key);
      // Usluge se ne šalju — one ne zauzimaju mesto u vozilu, pa bi samo
      // pokvarile procenu popunjenosti.
      draftStops.push({
        kind: 1, // CustomerAddress
        customerAddressId: stop.delivery.addressId ?? undefined,
        items: stop.delivery.items
          .filter((i) => !i.isService)
          .map((i) => ({
            articleCode: i.articleCode,
            articleName: i.articleName,
            quantity: i.quantity,
            erpDocument: stop.delivery.documentKey
          }))
      });
      continue;
    }

    if (stop.kind === 'preuzimanje') {
      if (!stop.stop.locationCode) continue;
      keys.push(stop.key);
      // Ista roba stoji i na kupčevoj stanici. Ovde se šalje da vozač zna
      // šta podiže; popunjenost i redosled utovara je namerno preskaču.
      draftStops.push({
        kind: 0,
        locationKind: stop.stop.locationKind === 'Warehouse' ? 1 : 0,
        locationCode: stop.stop.locationCode,
        isPickup: true,
        items: stop.stop.items.map((i) => ({
          articleCode: i.articleCode,
          articleName: i.articleName,
          quantity: i.quantity,
          erpDocument: i.erpDocument
        }))
      });
      continue;
    }

    // Tok: stanica ulazi na mesto prvog toka ka tom odredištu.
    const delivery = spojeno.get(stop.group.destination);
    if (!delivery || emitted.has(stop.group.destination)) continue;

    const code = codeOf(stop.group.destination);
    if (!code) continue;

    emitted.add(stop.group.destination);
    keys.push(delivery.flowKeys[0]);
    mergedKeys.set(delivery.flowKeys[0], delivery.flowKeys);
    draftStops.push({
      kind: 0,
      locationKind: kindOf(stop.group.destination),
      locationCode: code,
      items: delivery.items
    });
  }

  // --- Preuzimanja: roba koja ne stoji u polaznom magacinu ---------------
  //
  // Jedno preuzimanje po mestu, sa svom robom koja se tu uzima. Ulazi posle
  // stanice svog mesta ako je na turi (isto zaustavljanje), a UVEK pre prve
  // isporuke koju hrani. Kad ništa na turi ne hrani — sve ide u magacin —
  // ulazi na kraj: uzima se u povratku.
  const pickups = new Map<string, {
    locationKind: number; code: string; items: Stavka[]; feeds: string[]; flows: number;
  }>();

  for (const tok of tokovi) {
    if (!needsPickup(tok.group.origin, options.base)) continue;

    const originCode = codeOf(tok.group.origin!);
    if (!originCode) continue;

    let pickup = pickups.get(tok.group.origin!);
    if (!pickup) {
      pickup = {
        locationKind: kindOf(tok.group.origin!), code: originCode,
        items: [], feeds: [], flows: 0
      };
      pickups.set(tok.group.origin!, pickup);
    }
    pickup.items.push(...itemsOf(tok));
    pickup.flows += 1;

    if (delivers(tok)) {
      pickup.feeds.push(tok.group.destination);
    }
  }

  type Preuzimanje = NonNullable<ReturnType<typeof pickups.get>>;

  const insertPickup = (origin: string, pickup: Preuzimanje, at: number) => {
    draftStops.splice(at, 0, {
      kind: 0,
      locationKind: pickup.locationKind,
      locationCode: pickup.code,
      isPickup: true,
      items: pickup.items
    });
    keys.splice(at, 0, `pretovar:${origin}`);
  };

  /**
   * Pozicija sirovog ključa među složenim stanicama. Tok spojen u tuđu
   * stanicu pokazuje na stanicu svog odredišta.
   */
  const composedIndexOf = (rawKey: string): number => {
    const direct = keys.indexOf(rawKey);
    if (direct >= 0) return direct;

    for (const [first, all] of mergedKeys) {
      if (all.includes(rawKey)) return keys.indexOf(first);
    }
    return -1;
  };

  // Preuzimanja sa već dodeljenim mestom ulaze TAČNO tu: odmah ispred prve
  // stanice koja u datom redosledu stoji iza njih. „Složi redosled" ume da
  // preuzimanje stavi u prolaz ka daljem mestu — to mesto se mora sačuvati.
  const explicit = options.orderedKeys ?? [];
  const placed = new Set<string>();

  explicit.forEach((rawKey, position) => {
    if (!rawKey.startsWith('pretovar:')) return;

    const origin = rawKey.slice('pretovar:'.length);
    const pickup = pickups.get(origin);
    if (!pickup || placed.has(origin)) return;

    let at = keys.length;
    for (let i = position + 1; i < explicit.length; i++) {
      const index = composedIndexOf(explicit[i]);
      if (index >= 0) {
        at = index;
        break;
      }
    }

    insertPickup(origin, pickup, at);
    placed.add(origin);
  });

  // Ostala preuzimanja: uz stanicu svog mesta ako je na turi, a uvek pre
  // prve isporuke koju hrane; bez isporuke na turi — na kraj, u povratku.
  for (const [origin, pickup] of pickups) {
    if (placed.has(origin)) continue;

    const indexOfDelivery = (reference: string) => {
      const delivery = spojeno.get(reference);
      return delivery ? keys.indexOf(delivery.flowKeys[0]) : -1;
    };

    const ownStop = indexOfDelivery(origin);
    const fed = pickup.feeds.map(indexOfDelivery).filter((i) => i >= 0);
    const earliestFed = fed.length > 0 ? Math.min(...fed) : -1;

    let at: number;
    if (earliestFed >= 0) {
      at = ownStop >= 0 && ownStop < earliestFed ? ownStop + 1 : earliestFed;
    } else {
      at = ownStop >= 0 ? ownStop + 1 : keys.length;
    }

    insertPickup(origin, pickup, at);
  }

  const pickupSummary = new Map<string, { pieces: number; feeds: string[]; toWarehouse: boolean }>();
  for (const [origin, pickup] of pickups) {
    pickupSummary.set(`pretovar:${origin}`, {
      pieces: pickup.items.reduce((sum, item) => sum + item.quantity, 0),
      feeds: Array.from(new Set(pickup.feeds)),
      toWarehouse: pickup.feeds.length < pickup.flows
    });
  }

  // --- Tačke obilaska za mapu, istim redom kao draftStops ----------------
  const sequence: TourPoint[] = draftStops.map((draftStop, index) => {
    const key = keys[index];

    if (draftStop.kind === 1) {
      const kupac = selections.find(
        (s): s is Extract<TourStopSelection, { kind: 'kupac' }> =>
          s.kind === 'kupac' && s.key === key);
      return {
        key,
        label: kupac ? `${kupac.delivery.address}, ${kupac.delivery.city}` : 'kupac',
        latitude: kupac?.delivery.latitude ?? null,
        longitude: kupac?.delivery.longitude ?? null,
        isPickup: false
      };
    }

    if (key.startsWith('preuzimanje:')) {
      const b2c = selections.find(
        (s): s is Extract<TourStopSelection, { kind: 'preuzimanje' }> =>
          s.kind === 'preuzimanje' && s.key === key);
      return {
        key,
        label: b2c?.stop.label ?? 'preuzimanje',
        latitude: b2c?.stop.latitude ?? null,
        longitude: b2c?.stop.longitude ?? null,
        isPickup: true
      };
    }

    const location = options.locations.find(
      (l) => l.kind === (draftStop.locationKind === 1 ? 'Warehouse' : 'Store')
        && l.code === draftStop.locationCode);

    return {
      key,
      label: location?.name
        ?? `${draftStop.locationKind === 1 ? 'MAG' : 'MPO'} ${draftStop.locationCode}`,
      latitude: location?.latitude ?? null,
      longitude: location?.longitude ?? null,
      isPickup: draftStop.isPickup === true
    };
  });

  return { draftStops, keys, merged: mergedKeys, sequence, pickupSummary };
}
